import { cn } from '@/lib/utils';

import { PagedCarouselInstance } from './PagedCarouselInstance';
import {
  ItemsPerPage,
  PagedCarouselClassNames,
  PagedCarouselRenderItem,
} from './types';

export type PagedCarouselProps<ItemType> = {
  items: ItemType[];
  renderItem: PagedCarouselRenderItem<ItemType>;
  'aria-label': string;
  showEdgeFade?: boolean;
  /**
   * Show a glimpse of the next page/card at the edge (enables "peek" effect)
   */
  peekPreviewSurroundingPages?: boolean;
  /**
   * Items per page for each breakpoint. When the breakpoint also has
   * `itemWidth`, this is only an upper bound (the measured fit decides the
   * actual count) and may be omitted. Each breakpoint needs `itemsPerPage`,
   * `itemWidth`, or both.
   */
  itemsPerPage?: ItemsPerPage;
  /**
   * Fixed card width per breakpoint (any CSS length, e.g. `'280px'`). When
   * set, cards render at this width and pages auto-fit the viewport; when
   * omitted, cards are fluid and sized by `itemsPerPage`.
   */
  itemWidth?: { mobile?: string; desktop?: string };
  /** Gap between cards, in rem units (e.g. `1.5` renders as `1.5rem`). Defaults to `1.5`. */
  gap?: number;
  classNames?: PagedCarouselClassNames;
};

export function PagedCarousel<ItemType>({
  itemsPerPage,
  itemWidth,
  ...rest
}: PagedCarouselProps<ItemType>) {
  return (
    <>
      <div className={cn('w-full min-w-0 lg:hidden', rest.classNames?.root)}>
        <PagedCarouselInstance
          itemsPerPage={itemsPerPage?.mobile}
          itemWidth={itemWidth?.mobile}
          {...rest}
        />
      </div>
      <div
        className={cn('hidden w-full min-w-0 lg:block', rest.classNames?.root)}
      >
        <PagedCarouselInstance
          itemsPerPage={itemsPerPage?.desktop}
          itemWidth={itemWidth?.desktop}
          {...rest}
        />
      </div>
    </>
  );
}

export type { ItemsPerPage, PagedCarouselRenderItem };
